import {
  type AddRetroItemInput,
  type RetroItemToTaskInput,
  type SaveSprintReviewInput,
  type SprintRetroView,
  type SprintScopeSummary,
  type SprintView,
  type TaskKind,
  type TaskView,
  type UpdateRetroItemInput,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

/**
 * Dữ liệu cho tab Tổng quan — APM-ROLE-r.
 *
 * ## Vì sao mốc đọc qua `?kind=milestone`, không qua `GET /gantt`
 *
 * Mốc **là** task có `kind = 'milestone'`, và trước APM-ROLE-r cách duy nhất
 * lấy được `kind` ở client là gọi cả khung nhìn Gantt — tức chạy CPM toàn dự án
 * để hiện vài dòng. Tổng quan là tab **mặc định**, nên nó chạy ở mọi lần mở một
 * dự án; trả giá đó ở đúng đường nóng nhất là sai chỗ.
 */
export const overviewApi = {
  milestones: (projectId: string, sprintId?: string) =>
    apiFetch<TaskView[]>(
      `/projects/${projectId}/tasks?kind=${'milestone' satisfies TaskKind}${
        sprintId ? `&sprintId=${sprintId}` : ''
      }`,
    ),
};

export const overviewKeys = {
  milestones: (projectId: string, sprintId?: string) =>
    ['projects', projectId, 'milestones', sprintId ?? 'all'] as const,
};

export const sprintHistoryApi = {
  list: (projectId: string) => apiFetch<SprintView[]>(`/projects/${projectId}/sprints`),

  /** Bốn con số kể lại chuyện một sprint — APM-AGILE-b. */
  scope: (sprintId: string) => apiFetch<SprintScopeSummary>(`/sprints/${sprintId}/scope`),

  /** Việc của **một** sprint. Lọc ở server — xem `TasksController.list`. */
  tasks: (projectId: string, sprintId: string) =>
    apiFetch<TaskView[]>(`/projects/${projectId}/tasks?sprintId=${sprintId}`),
};

export const sprintHistoryKeys = {
  list: (projectId: string) => ['sprints', projectId] as const,
  tasks: (projectId: string, sprintId: string) =>
    ['sprints', projectId, sprintId, 'tasks'] as const,
  scope: (sprintId: string) => ['sprints', sprintId, 'scope'] as const,
};

/** Biên bản review và retrospective của một sprint — APM-AGILE-c. */
export const retroApi = {
  view: (sprintId: string) => apiFetch<SprintRetroView>(`/sprints/${sprintId}/retro`),

  saveReview: (sprintId: string, body: SaveSprintReviewInput) =>
    apiFetch<SprintRetroView>(`/sprints/${sprintId}/review`, { method: 'PUT', body }),

  addItem: (sprintId: string, body: AddRetroItemInput) =>
    apiFetch<SprintRetroView>(`/sprints/${sprintId}/retro-items`, { method: 'POST', body }),

  updateItem: (itemId: string, body: UpdateRetroItemInput) =>
    apiFetch<SprintRetroView>(`/retro-items/${itemId}`, { method: 'PATCH', body }),

  removeItem: (itemId: string) =>
    apiFetch<SprintRetroView>(`/retro-items/${itemId}`, { method: 'DELETE' }),

  toTask: (itemId: string, body: RetroItemToTaskInput) =>
    apiFetch<SprintRetroView>(`/retro-items/${itemId}/task`, { method: 'POST', body }),
};

export const retroKeys = {
  view: (sprintId: string) => ['sprints', sprintId, 'retro'] as const,
};
